// src/pages/PublicComparePage.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import Footer from '../components/common/Footer';
import { Input } from '../components/common/Input';
import { useLang } from '../context/LangContext';
import { checkCompareCode, prepareComparison, generateComparison } from '../api/results';
import { COMPARISON_PURPOSES } from '../api/comparisonPurposes';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Center = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const Box = styled(Card)`
  width: 100%;
  max-width: 560px;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Field = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  label {
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textLight};
    margin-bottom: ${({ theme }) => theme.spacing.xs};
  }
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.85rem;
  margin: 4px 0 0;
`;

const InfoText = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 0.95rem;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

  const normalize = (value: string) => value.trim().toUpperCase();
const isValidCode = (code: string) => /^[A-Z0-9]{6,15}$/.test(code);

const PublicComparePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const [code1, setCode1] = useState('');
  const [code2, setCode2] = useState('');
  const [error1, setError1] = useState('');
  const [error2, setError2] = useState('');
  const [formError, setFormError] = useState('');
  const [test1, setTest1] = useState('');
  const [test2, setTest2] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const [existingComparison, setExistingComparison] = useState<{ code: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [purposeCode, setPurposeCode] = useState('');
  const [purposeDescription, setPurposeDescription] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setError1('');
    setError2('');
    setTest1('');
    setTest2('');
    setMismatch(false);
    setExistingComparison(null);

    const c1 = normalize(code1);
    const c2 = normalize(code2);
    if (!isValidCode(c1) || !isValidCode(c2)) {
      setFormError(t('resultCodeInvalid'));
      return;
    }
    if (c1 === c2) {
      setFormError(t('compareSameCode'));
      return;
    }

    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        checkCompareCode(c1),
        checkCompareCode(c2),
      ]);
      setError1(r1.found ? '' : (r1.code_type === 'comparison'
        ? t('compareCodeIsComparison').replace('{code}', c1)
        : t('compareMissing').replace('{code}', c1)));
      setError2(r2.found ? '' : (r2.code_type === 'comparison'
        ? t('compareCodeIsComparison').replace('{code}', c2)
        : t('compareMissing').replace('{code}', c2)));
      const comparisonCode = r1.code_type === 'comparison' ? c1
        : r2.code_type === 'comparison' ? c2 : null;
      setExistingComparison(comparisonCode ? { code: comparisonCode } : null);
      setTest1(r1.found && r1.test_title ? r1.test_title : '');
      setTest2(r2.found && r2.test_title ? r2.test_title : '');
      if (!r1.found || !r2.found) {
        setLoading(false);
        return;
      }
      // Тесты разных типов — сравнивать нельзя (публичный флоу).
      // Редакции одной методики (разные test_id, одно название) считаются одним тестом,
      // приоритет — сравнение по названию; test_id — fallback, если названий нет.
      const sameTest = r1.test_title && r2.test_title
        ? r1.test_title.toLowerCase() === (r2.test_title || '').toLowerCase()
        : !!(r1.test_id && r2.test_id && r1.test_id === r2.test_id);
      if (!sameTest) {
        setMismatch(true);
        setFormError(t('compareDifferentTest').replace('{t1}', r1.test_title || '?').replace('{t2}', r2.test_title || '?'));
        setLoading(false);
        return;
      }
      const prepared = await prepareComparison(c1, c2, {
        purpose_code: purposeCode || null,
        purpose_description: purposeDescription.trim() || null,
        additional_notes: null,
      });
      await generateComparison(prepared.compare_code);
      navigate(`/compare-result/${prepared.compare_code}`);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setFormError(err?.response?.data?.detail || t('compareLoadError'));
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Center>
        <Box>
          <Button variant="text" size="sm" onClick={() => navigate('/results-gates')} style={{ marginBottom: 16 }}>
            {t('compareResultBack')}
          </Button>
          <Title>{t('comparePageTitle')}</Title>
          <InfoText>{t('compareProfilesHint')}</InfoText>
          <form onSubmit={handleSubmit}>
            <Field>
              <label htmlFor="compare-code-1">{t('compareCode1Label')}</label>
              <Input
                id="compare-code-1"
                value={code1}
                onChange={(e) => { setCode1(e.target.value.toUpperCase()); setError1(''); setTest1(''); setFormError(''); setMismatch(false); setExistingComparison(null); }}
                maxLength={15}
                fullWidth
              />
              {test1 && <InfoText>{t('compareTestLabel')}: {test1}</InfoText>}
              {error1 && <ErrorText>{error1}</ErrorText>}
            </Field>
            <Field>
            <label htmlFor="compare-code-2">{t('compareCode2Label')}</label>
            <Input
              id="compare-code-2"
              value={code2}
              onChange={(e) => { setCode2(e.target.value.toUpperCase()); setError2(''); setTest2(''); setFormError(''); setMismatch(false); setExistingComparison(null); }}
              maxLength={15}
              fullWidth
            />
            {test2 && <InfoText>{t('compareTestLabel')}: {test2}</InfoText>}
            {error2 && <ErrorText>{error2}</ErrorText>}
          </Field>
          {mismatch && <ErrorText>{formError}</ErrorText>}
          {!mismatch && formError && <ErrorText>{formError}</ErrorText>}
          {existingComparison && (
            <div style={{ marginTop: 16 }}>
              <Button variant="outline" size="lg" fullWidth onClick={() => navigate(`/compare-result/${encodeURIComponent(existingComparison.code)}`)}>
                {t('compareOpenExisting')}
              </Button>
            </div>
          )}
          <Field>
            <label htmlFor="compare-purpose">{t('comparePurposeLabel')}</label>
            <Select
              id="compare-purpose"
              value={purposeCode}
              onChange={(e) => setPurposeCode(e.target.value)}
            >
              <option value="">{t('comparePurposeAny')}</option>
              {COMPARISON_PURPOSES.map((p) => (
                <option key={p.code} value={p.code}>{p.label}</option>
              ))}
            </Select>
          </Field>
          {purposeCode && (
            <Field>
              <label htmlFor="compare-purpose-desc">{t('comparePurposeDescriptionLabel')}</label>
              <textarea
                id="compare-purpose-desc"
                value={purposeDescription}
                onChange={(e) => setPurposeDescription(e.target.value)}
                rows={3}
                placeholder={t('comparePurposeDescriptionPlaceholder')}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid ' + 'var(--border, #ddd)',
                  borderRadius: 8,
                  fontSize: '0.95rem',
                  resize: 'vertical',
                }}
              />
              <InfoText style={{ marginTop: 4, marginBottom: 0 }}>{t('comparePurposeConfirm')}</InfoText>
            </Field>
          )}
          <div style={{ marginTop: 20 }}>
            <Button variant="primary" size="lg" type="submit" fullWidth disabled={loading || mismatch}>
              {loading ? t('genLoading') : t('compareGetResultBtn')}
            </Button>
          </div>
        </form>
        </Box>
      </Center>
      <Footer />
    </PageContainer>
  );
};

export default PublicComparePage;
